import { useInfiniteQuery, useQuery, useQueries } from "@tanstack/react-query";
import api from "../../../services/api";
import { getFactoryFacilities, getVendorFacilities } from "../../../services/kwsService";
import { GRINGOTTS_BASE_URL, PAYMENT_BASE_URL, VIP_VENDOR_FACILITY_BASE, walletKeys } from "./queryKeys";
import type {
  WalletDateRange,
  WalletEntriesResponse,
  WalletEntriesResult,
  WalletSummaryDTO,
  WalletSummaryResponse,
  WalletTransactionsDTO,
  WalletTransactionsParams,
  WalletTransactionsSearchResponse,
} from "../types/wallet";

export interface VendorWalletBalance {
  vendor_facility_id: number;
  wallet_id: number;
  balance: number;
  total_credit: number;
  total_debit: number;
  currency: string;
}

export interface FacilityDetail {
  id: number;
  name: string;
  /**
   * Gringotts wallet id, or null when no wallet has been provisioned for the
   * facility yet. The Wallet tab renders those as a "no wallet yet" row;
   * VendorWalletModal filters them out (it only lists balances).
   */
  walletId: number | null;
}

// ---------------------------------------------------------------------------
// Raw async functions
// ---------------------------------------------------------------------------

export async function getWalletBalance(
  facilityId: number,
): Promise<VendorWalletBalance | null> {
  try {
    return await api.get(`${VIP_VENDOR_FACILITY_BASE}/${facilityId}/wallet`);
  } catch {
    return null;
  }
}

async function fetchFacilityDetails(factoryCode: string): Promise<FacilityDetail[]> {
  const ids: number[] = await getFactoryFacilities(factoryCode);
  const validIds = (ids ?? []).filter((id) => id > 0);
  if (validIds.length === 0) return [];
  const response = await getVendorFacilities(validIds);
  const records: any[] = response?.records ?? [];
  // Facilities without a wallet are kept, carrying walletId: null — the Wallet
  // tab has to show them as "no wallet yet" rather than pretend they don't exist.
  // Callers that only want balances (VendorWalletModal) filter on walletId.
  return records
    .filter((r) => r.id > 0)
    .map((r) => ({
      id: r.id,
      name: r.name ?? `Facility #${r.id}`,
      walletId: r.wallet_id ?? null,
    }));
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetches every facility of a factory with its name and wallet id.
 * Used by VendorWalletModal (names instead of raw IDs) and by the Wallet tab
 * (which also needs the wallet-less facilities).
 */
export function useFacilityDetails(factoryCode: string | undefined) {
  return useQuery({
    queryKey: walletKeys.facilityDetails(factoryCode ?? ""),
    queryFn: () => fetchFacilityDetails(factoryCode!),
    enabled: !!factoryCode && factoryCode !== "all",
    retry: 1,
  });
}

/**
 * Fetches the wallet balance for every facility in the provided list.
 * Results maintain index parity with the input array — result[i] corresponds to
 * facilityIds[i]. VendorWalletModal uses vendor_facility_id from the response body
 * as the primary key; facilityIds[i] serves as an index-based fallback if that field
 * is absent from a response.
 */
export function useFactoryWallets(facilityIds: number[]) {
  return useQueries({
    queries: facilityIds.map((id) => ({
      queryKey: walletKeys.balance(id),
      queryFn: () => getWalletBalance(id),
      enabled: id > 0,
    })),
  });
}

// ---------------------------------------------------------------------------
// Gringotts ledger (ENG-27365)
// ---------------------------------------------------------------------------
//
// Read directly through the gateway rather than proxied by VIP, whose wallet
// transactions endpoint is deferred (ADR 0023 §15). Verified from the app on
// beta with the app's own token.
//
// ⚠️ Gringotts' own read authorization is an open gap (its WalletsController
// carries no auth annotation), so never surface a walletId outside the factory
// the user has selected. Tracked on ENG-27279.

function rangeParams(range: WalletDateRange | undefined): URLSearchParams {
  const sp = new URLSearchParams();
  if (range?.startDate) sp.set("startDate", range.startDate);
  if (range?.endDate) sp.set("endDate", range.endDate);
  return sp;
}

/**
 * Wallet totals for a window.
 *
 * Unwraps the `walletDTOList[0]` envelope — the figures are nested exactly as
 * they are on the transactions search. Returns an empty object rather than
 * undefined when the list is absent, so callers never branch on the envelope.
 */
export async function getWalletSummary(
  walletId: number,
  range?: WalletDateRange,
): Promise<WalletSummaryDTO> {
  const sp = rangeParams(range);
  const query = sp.toString();
  const response: WalletSummaryResponse = await api.get(
    `${GRINGOTTS_BASE_URL}/${walletId}/summary${query ? `?${query}` : ""}`,
  );
  return response?.walletDTOList?.[0] ?? {};
}

/**
 * Newest first, in the order balances were applied. Gringotts applies each
 * row's balance under a wallet lock and stamps `updatedAt` in the same save,
 * so near-simultaneous rows can be applied out of `transactionDate` and id
 * order; sorting on `updatedAt` keeps each row's balance snapshot following
 * from the one before it (ENG-27798). `id` breaks ties.
 */
export const LEDGER_SORT = "updatedAt:desc,id:desc";

/**
 * One page of transactions in a single direction.
 *
 * `transferType` is mandatory on the Gringotts search — a full ledger is two
 * calls merged client-side (see `mergeLedgerPages`).
 *
 * Unwraps the `walletTransactionsDTOList[0]` envelope, returning an empty page
 * rather than undefined when the list is absent, so callers never branch on
 * the envelope shape.
 */
export async function getWalletTransactions(
  walletId: number,
  params: WalletTransactionsParams,
): Promise<WalletTransactionsDTO> {
  const sp = rangeParams(params);
  sp.set("transferType", params.transferType);
  // Without an explicit sort Gringotts returns rows in no useful order, so the
  // first page could miss the latest transactions entirely.
  sp.set("sortBy", LEDGER_SORT);
  if (params.pageNumber != null) sp.set("pageNumber", String(params.pageNumber));
  if (params.pageSize != null) sp.set("pageSize", String(params.pageSize));

  const response: WalletTransactionsSearchResponse = await api.get(
    `${GRINGOTTS_BASE_URL}/${walletId}/transactions/search?${sp.toString()}`,
  );
  return (
    response?.walletTransactionsDTOList?.[0] ?? {
      walletId,
      totalCount: 0,
      transactionsModelList: [],
    }
  );
}

/**
 * Wallet totals for a date window. Disabled until a wallet exists — a facility
 * with `wallet_id: null` has nothing to read.
 */
export function useWalletSummary(
  walletId: number | null | undefined,
  range?: WalletDateRange,
) {
  return useQuery({
    queryKey: walletKeys.summary(walletId ?? 0, range ?? {}),
    queryFn: () => getWalletSummary(walletId!, range),
    enabled: walletId != null && walletId > 0,
  });
}

/** One direction of the ledger, paged. Disabled until a wallet exists. */
export function useWalletTransactions(
  walletId: number | null | undefined,
  params: WalletTransactionsParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: walletKeys.transactions(walletId ?? 0, params),
    queryFn: () => getWalletTransactions(walletId!, params),
    enabled: walletId != null && walletId > 0 && options?.enabled !== false,
    keepPreviousData: true,
  });
}

// ---------------------------------------------------------------------------
// Open-advance entries (ENG-27316)
// ---------------------------------------------------------------------------

/**
 * The states that make up a factory's live open advance. They are bounded by
 * the hold period (an entry is open or expired for weeks, not forever), so one
 * capped page always holds all of them — they drive the cards, the ageing card
 * and the default table view.
 */
export const ACTIVE_ENTRY_STATES = ["OPEN", "EXPIRED"];

/** Settled entries. These accumulate forever, so they are paged on demand. */
export const HISTORY_ENTRY_STATES = ["REFUNDED", "CONSUMED"];

/** VIP's server-side page cap — the active list fits in one. */
export const WALLET_ENTRIES_LIMIT = 500;

/** History rows per "Load more". */
export const WALLET_HISTORY_PAGE_SIZE = 20;

export interface WalletEntriesQuery {
  states: string[];
  limit: number;
  offset?: number;
  /** Extra blocks to return alongside the page, e.g. `facilities` totals. */
  include?: string[];
}

/**
 * One page of a factory's open-advance entries in the given states, newest
 * first.
 *
 * Two answers mean "open advance isn't available here" rather than an error,
 * and resolve to `{enabled: false}` so the Wallet tab renders exactly as it did
 * before open advance:
 * - 503 — VIP has the flow but `payment.open_advance.enabled` is off;
 * - 404 — this VIP predates the flow (vip#337), so the route doesn't exist.
 *   Out-of-scope facilities never 404 here: VIP returns an empty list for them.
 */
export async function getWalletEntries(
  factoryCode: string,
  { states, limit, offset = 0, include = [] }: WalletEntriesQuery,
): Promise<WalletEntriesResult> {
  const sp = new URLSearchParams({
    factory_code: factoryCode,
    state: states.join(","),
    limit: String(limit),
    offset: String(offset),
  });
  if (include.length) sp.set("include", include.join(","));
  try {
    const response: WalletEntriesResponse = await api.get(
      `${PAYMENT_BASE_URL}/wallet/entries?${sp.toString()}`,
    );
    const result: WalletEntriesResult = { enabled: true, entries: response?.entries ?? [] };
    // Absent on a VIP that predates the totals (ENG-27744): the cards that need
    // them stay hidden rather than reading zero.
    if (response?.facilities) result.facilities = response.facilities;
    return result;
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 503 || status === 404) return { enabled: false, entries: [] };
    throw error;
  }
}

/**
 * A factory's active open advance (Open + Expired) and its per-facility totals,
 * in one request — the totals don't depend on the page, so only this query asks
 * for them. No retry: a 503/404 is already data, and a failure should not stall
 * the tab behind three attempts.
 */
export function useWalletEntries(factoryCode: string | undefined) {
  return useQuery({
    queryKey: walletKeys.entries(factoryCode ?? ""),
    queryFn: () => getWalletEntries(factoryCode!, {
        states: ACTIVE_ENTRY_STATES,
        limit: WALLET_ENTRIES_LIMIT,
        include: ["facilities"],
      }),
    enabled: !!factoryCode && factoryCode !== "all",
    retry: false,
  });
}

/**
 * Settled entries (refunded / used up), a page at a time, fetched only once the
 * user asks for them. A short page means there is no more.
 */
export function useWalletEntryHistory(
  factoryCode: string | undefined,
  states: string[],
  enabled: boolean,
) {
  return useInfiniteQuery({
    queryKey: walletKeys.entryHistory(factoryCode ?? "", states),
    queryFn: ({ pageParam = 0 }) =>
      getWalletEntries(factoryCode!, { states, limit: WALLET_HISTORY_PAGE_SIZE, offset: pageParam }),
    getNextPageParam: (lastPage, pages) =>
      lastPage.entries.length < WALLET_HISTORY_PAGE_SIZE ? undefined : pages.length * WALLET_HISTORY_PAGE_SIZE,
    enabled: enabled && !!factoryCode && factoryCode !== "all",
    retry: false,
  });
}
